import type {
  AssignFiscalBody,
  CompanySummary,
  ContratosResult,
  CreateObraBody,
  EscopoDeContratos,
  ListObrasQuery,
  MapObrasQuery,
  ObraDetail,
  ObraFilterOptions,
  ObraListResult,
  ObraMapResult,
  Paginated,
  UpdateObraBody,
} from '@obras/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryString } from '../../lib/api.js';

export const obraKeys = {
  all: ['obras'] as const,
  list: (filters: Partial<ListObrasQuery>) => ['obras', 'list', filters] as const,
  map: (filters: Partial<MapObrasQuery>) => ['obras', 'map', filters] as const,
  contratos: (escopo: EscopoDeContratos) => ['obras', 'contratos', escopo] as const,
  detail: (id: string) => ['obras', 'detail', id] as const,
};

/** Lista vazia vira `undefined`: o parâmetro some da URL em vez de ir em branco. */
const csv = (values: readonly string[] | undefined): string | undefined =>
  values && values.length > 0 ? values.join(',') : undefined;

/** O recorte na URL — o mesmo na lista paginada e no mapa. */
function recorteNaUrl(filters: Partial<MapObrasQuery>) {
  return {
    search: filters.search,
    statuses: csv(filters.statuses),
    fiscalIds: csv(filters.fiscalIds),
    companyIds: csv(filters.companyIds),
    kinds: csv(filters.kinds),
    pendencias: csv(filters.pendencias),
    sort: filters.sort,
  };
}

/** `enabled` desliga a consulta enquanto a lista está no modo mapa. */
export function useObras(filters: Partial<ListObrasQuery>, enabled = true) {
  return useQuery({
    queryKey: obraKeys.list(filters),
    queryFn: ({ signal }) =>
      api.get<ObraListResult>(
        `/obras${queryString({
          page: filters.page ?? 1,
          perPage: filters.perPage ?? 24,
          ...recorteNaUrl(filters),
        })}`,
        signal,
      ),
    // Mantém a página anterior visível durante a busca: evita a lista piscar
    // a cada tecla digitada — design.md §8.
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * O recorte inteiro, com o ponto de cada obra, para o modo mapa.
 *
 * Só consulta com o mapa à mostra: quem fica nos cartões não paga por ele.
 */
export function useObrasNoMapa(filters: Partial<MapObrasQuery>, enabled = true) {
  return useQuery({
    queryKey: obraKeys.map(filters),
    queryFn: ({ signal }) =>
      api.get<ObraMapResult>(`/obras/mapa${queryString(recorteNaUrl(filters))}`, signal),
    // O mapa anterior fica na tela enquanto o novo recorte chega.
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * Os contratos do escopo, para a planilha de contratos — inteiros, sem página:
 * busca, situação e ordem correm na tela. Debaixo de `obras`, para criar ou
 * editar uma obra refazer a planilha junto com a lista.
 */
export function useContratos(escopo: EscopoDeContratos) {
  return useQuery({
    queryKey: obraKeys.contratos(escopo),
    queryFn: ({ signal }) =>
      api.get<ContratosResult>(`/obras/contratos${queryString({ escopo })}`, signal),
    // Trocar de escopo mantém a planilha anterior até a nova chegar.
    placeholderData: (previous) => previous,
  });
}

/** Opções dos filtros: só fiscais e empresas que aparecem em alguma obra. */
export function useObraFilterOptions() {
  return useQuery({
    queryKey: ['obras', 'filtros'],
    queryFn: ({ signal }) => api.get<ObraFilterOptions>('/obras/filtros', signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function useObra(id: string) {
  return useQuery({
    queryKey: obraKeys.detail(id),
    queryFn: ({ signal }) => api.get<ObraDetail>(`/obras/${id}`, signal),
  });
}

export function useCreateObra() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateObraBody) => api.post<ObraDetail>('/obras', body),
    onSuccess: (obra) => {
      queryClient.setQueryData(obraKeys.detail(obra.id), obra);
      void queryClient.invalidateQueries({ queryKey: obraKeys.all });
    },
  });
}

export function useUpdateObra(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateObraBody) => api.patch<ObraDetail>(`/obras/${id}`, body),
    onSuccess: (obra) => {
      queryClient.setQueryData(obraKeys.detail(obra.id), obra);
      void queryClient.invalidateQueries({ queryKey: obraKeys.all });
    },
  });
}

export function useDeleteObra() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/obras/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: obraKeys.all }),
  });
}

export function useCompanyOptions() {
  return useQuery({
    queryKey: ['empresas', 'options'],
    queryFn: ({ signal }) =>
      api.get<Paginated<CompanySummary>>('/empresas?perPage=100&status=ativa', signal),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Designa alguém na obra, ou muda o papel de quem já está.
 *
 * O mesmo `POST` faz as duas coisas: o servidor grava por conflito na chave
 * (obra, pessoa). Promover a responsável rebaixa quem era antes — é uma vaga
 * só, e deixar duas seria o mesmo que não ter nenhuma.
 */
export function useAssignFiscal(obraId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AssignFiscalBody) => api.post<ObraDetail>(`/obras/${obraId}/fiscais`, body),
    onSuccess: (obra) => {
      queryClient.setQueryData(obraKeys.detail(obraId), obra);
      void queryClient.invalidateQueries({ queryKey: obraKeys.all });
    },
  });
}

export function useRemoveFiscal(obraId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => api.delete<ObraDetail>(`/obras/${obraId}/fiscais/${userId}`),
    onSuccess: (obra) => {
      queryClient.setQueryData(obraKeys.detail(obraId), obra);
      void queryClient.invalidateQueries({ queryKey: obraKeys.all });
    },
  });
}
